/**
 * JournalEntryStep.tsx - 日記輸入步驟（雙階段容器）
 *
 * 新 Day Close 流程中的日記功能：
 * - Phase 1: 用戶輸入日記內容
 * - Phase 2: AI 分析並給予回饋
 */
import React, { useState, useEffect, useCallback } from 'react';
import { DayCloseStepProps } from '../../../types/dayClose';
import { createCapture } from '../../../services/api/captures';
import { useDayCloseAPI } from '../../../hooks/useDayCloseAPI';
import JournalInput from './JournalInput';
import JournalFeedback from './JournalFeedback';
import { logger } from '../../../utils/logger';

const JournalEntryStep: React.FC<DayCloseStepProps> = ({
  dayCloseId,
  onComplete,
  onSkip,
  userId,
  setLoading,
  setCanContinue,
  setStepHandlers,
  dayCloseData
}) => {
  const { saveReflectionContent, loadReflectionContent } = useDayCloseAPI();
  const [phase, setPhase] = useState<'input' | 'feedback'>('input');
  const [journalContent, setJournalContent] = useState('');
  const [currentInputContent, setCurrentInputContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadedExisting, setHasLoadedExisting] = useState(false);

  // Handle input phase completion
  const handleInputComplete = useCallback(async (content: string) => {
    const trimmedContent = content.trim();
    const originalContent = journalContent.trim();

    // 如果沒有內容或內容沒有變化，直接跳過
    if (!trimmedContent || trimmedContent === originalContent) {
      onComplete();
      return;
    }

    try {
      setIsSubmitting(true);
      setLoading?.(true);

      // 1. Create Capture
      const captureData = {
        content: trimmedContent,
        userId,
        tags: ['day_close', 'journal']
      };

      const result = await createCapture(captureData);

      // 2. Save Capture ID to Day Close record
      if (result && result.id) {
        await saveReflectionContent(dayCloseId, result.id);
      }

      // 3. Save content and enter feedback phase
      setJournalContent(trimmedContent);
      setPhase('feedback');

    } catch (error) {
      logger.error('JournalEntryStep', 'Error saving journal', error);
      // Even if save fails, enter feedback phase
      setJournalContent(trimmedContent);
      setPhase('feedback');
    } finally {
      setIsSubmitting(false);
      setLoading?.(false);
    }
  }, [dayCloseId, userId, saveReflectionContent, onComplete, setLoading, journalContent]);

  // Handle input phase skip
  const handleInputSkip = useCallback(() => {
    onSkip?.();
  }, [onSkip]);

  // Handle feedback phase completion
  const handleFeedbackComplete = useCallback(() => {
    onComplete();
  }, [onComplete]);

  // Handle feedback phase skip
  const handleFeedbackSkip = useCallback(() => {
    onComplete();
  }, [onComplete]);

  // Set different step handlers based on phase
  useEffect(() => {
    if (setStepHandlers) {
      if (phase === 'input') {
        setStepHandlers({
          handleReflection: async () => {
            await handleInputComplete(currentInputContent);
          }
        });
      } else {
        setStepHandlers({
          handleReflection: async () => {
            handleFeedbackComplete();
          }
        });
      }
    }
  }, [setStepHandlers, phase, handleFeedbackComplete, handleInputComplete, currentInputContent]);

  // 載入現有的 journal 內容
  useEffect(() => {
    const loadExistingJournal = async () => {
      if (!dayCloseData?.reflectionCaptureId || hasLoadedExisting) return;

      try {
        setLoading?.(true);
        const capture = await loadReflectionContent(dayCloseData.reflectionCaptureId);

        if (capture?.content) {
          setJournalContent(capture.content);
          setCurrentInputContent(capture.content);
          // 保持在 input 階段讓用戶檢視和修改內容
        }

        setHasLoadedExisting(true);
      } catch (error) {
        logger.error('JournalEntryStep', 'Failed to load existing journal', error);
        setHasLoadedExisting(true);
      } finally {
        setLoading?.(false);
      }
    };

    loadExistingJournal();
  }, [dayCloseData, loadReflectionContent, hasLoadedExisting, setLoading]);

  // Monitor phase changes and update button state
  useEffect(() => {
    // This step can always be skipped or continued
    setCanContinue?.(true);
  }, [phase, setCanContinue]);

  // Render different sub-components based on phase
  if (phase === 'input') {
    return (
      <JournalInput
        userId={userId}
        content={currentInputContent}
        onContentChange={setCurrentInputContent}
        isLoading={isSubmitting}
      />
    );
  }

  if (phase === 'feedback') {
    return (
      <JournalFeedback
        journalContent={journalContent}
        dayCloseId={dayCloseId}
        userId={userId}
        onComplete={handleFeedbackComplete}
        onSkip={handleFeedbackSkip}
      />
    );
  }

  return null;
};

export default JournalEntryStep;
